import { BrowserRouter, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { getToken, clearToken, api } from './lib/api';
import Login from './pages/Login';
import Users from './pages/Users';
import Analytics from './pages/Analytics';
import Builds from './pages/Builds';
import AIRequests from './pages/AIRequests';
import AuditLog from './pages/AuditLog';
import Models from './pages/Models';
import Feedback from './pages/Feedback';
import Settings from './pages/Settings';
import SystemPrompts from './pages/SystemPrompts';

function RequireAuth({ children }: { children: React.ReactElement }) {
  if (!getToken()) return <Navigate to="/login" replace />;
  return children;
}

const NAV = [
  { to: '/', label: 'Analytics', icon: '📊', end: true },
  { to: '/users', label: 'Users', icon: '👥', end: false },
  { to: '/builds', label: 'Builds', icon: '📦', end: false },
  { to: '/ai-requests', label: 'AI Requests', icon: '🤖', end: false },
  { to: '/models', label: 'Models', icon: '🧠', end: false },
  { to: '/feedback', label: 'Feedback', icon: '💬', end: false },
  { to: '/audit-log', label: 'Audit Log', icon: '📋', end: false },
  { to: '/prompts', label: 'Prompts', icon: '📝', end: false },
  { to: '/settings', label: 'Settings', icon: '⚙️', end: false },
];

function Shell({ children }: { children: React.ReactNode }) {
  function handleLogout() {
    // Clear httpOnly cookie on server, then clear client-side indicator
    api.logoutBrowser().catch(() => {}).finally(() => {
      clearToken();
      window.location.href = '/login';
    });
  }

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar */}
      <aside className="w-56 bg-white border-r flex flex-col shrink-0">
        <div className="p-5 border-b">
          <span className="font-bold text-gray-900 text-base">OSPI Admin</span>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map(({ to, label, icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`
              }
            >
              <span>{icon}</span>
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          >
            <span>🚪</span>
            Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {children}
        </div>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/*"
          element={
            <RequireAuth>
              <Shell>
                <Routes>
                  <Route path="/" element={<Analytics />} />
                  <Route path="/users" element={<Users />} />
                  <Route path="/builds" element={<Builds />} />
                  <Route path="/ai-requests" element={<AIRequests />} />
                  <Route path="/models" element={<Models />} />
                  <Route path="/feedback" element={<Feedback />} />
                  <Route path="/audit-log" element={<AuditLog />} />
                  <Route path="/prompts" element={<SystemPrompts />} />
                  <Route path="/settings" element={<Settings />} />
                </Routes>
              </Shell>
            </RequireAuth>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
